import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  AuthService,
  User,
  Organization,
  LoginCredentials,
  VerifyEmailData,
  GoogleAuthData,
  UpdateProfileData,
  AxiosErrorResponse,
  CreateOrganizationData,
} from "@/lib/api/services/authService";

interface SiteRoleData {
  role: string;
  isOrganizationOwner: boolean;
  timestamp: number;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  requiresVerification: boolean;
  verificationEmail: string | null;
  organizations: Organization[];
  currentOrganization: Organization | null;
  onboardingCompleted: boolean;
  welcomeCompleted: boolean;
  siteRoles: Record<number, SiteRoleData>;
}

const storage = {
  getItem: (key: string): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(key);
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(key, value);
  },
  removeItem: (key: string): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(key);
  },
};

const FORCED_LOGOUT_COOKIE = "forced_logout";

const clearForcedLogoutCookie = (): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${FORCED_LOGOUT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
};

const loadInitialState = (): AuthState => {
  let user = null;
  let organizations: Organization[] = [];
  let currentOrganization: Organization | null = null;
  let onboardingCompleted = false;
  let welcomeCompleted = false;

  if (typeof window !== "undefined") {
    const userStr = storage.getItem("user");
    const organizationsStr = storage.getItem("organizations");
    const currentOrgStr = storage.getItem("currentOrganization");

    if (userStr) {
      try {
        user = JSON.parse(userStr);
        welcomeCompleted = user?.isWelcomeCompleted ?? false;
        onboardingCompleted = user?.onboardingCompleted ?? false;
      } catch (error) {
        console.error("Failed to parse user:", error);
      }
    }

    if (organizationsStr) {
      try {
        organizations = JSON.parse(organizationsStr);
      } catch (error) {
        console.error("Failed to parse organizations:", error);
      }
    } else if (user?.organizations) {
      organizations = user.organizations;
      storage.setItem("organizations", JSON.stringify(organizations));
    }

    if (currentOrgStr) {
      try {
        currentOrganization = JSON.parse(currentOrgStr);
      } catch (error) {
        console.error("Failed to parse current organization:", error);
      }
    } else if (organizations.length > 0) {
      currentOrganization = organizations[0];
      storage.setItem(
        "currentOrganization",
        JSON.stringify(currentOrganization),
      );
    }
  }

  return {
    user,
    isLoading: false,
    error: null,
    isAuthenticated: !!user,
    isLoggingOut: false,
    requiresVerification: false,
    verificationEmail: null,
    organizations,
    currentOrganization,
    onboardingCompleted,
    welcomeCompleted,
    siteRoles: {},
  };
};

const defaultInitialState: AuthState = {
  user: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  isLoggingOut: false,
  requiresVerification: false,
  verificationEmail: null,
  organizations: [],
  currentOrganization: null,
  onboardingCompleted: false,
  welcomeCompleted: false,
  siteRoles: {},
};

const initialState: AuthState =
  typeof window !== "undefined" ? loadInitialState() : defaultInitialState;

const handleApiError = (error: unknown): string => {
  if (!error) return "An unexpected error occurred";

  const axiosError = error as AxiosErrorResponse;
  if (axiosError?.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }

  if (axiosError?.message) {
    return axiosError.message;
  }

  return "An unexpected error occurred";
};

const saveAuthToStorage = (
  user: User | null,
  organizations: Organization[] = [],
  currentOrganization: Organization | null = null,
) => {
  try {
    clearForcedLogoutCookie();

    if (user) {
      storage.setItem("user", JSON.stringify(user));
    }

    storage.setItem("organizations", JSON.stringify(organizations));

    if (currentOrganization) {
      storage.setItem(
        "currentOrganization",
        JSON.stringify(currentOrganization),
      );
    } else {
      if (organizations.length > 0) {
        storage.setItem(
          "currentOrganization",
          JSON.stringify(organizations[0]),
        );
      }
    }
  } catch (error) {
    console.error("Error saving auth to storage:", error);
  }
};

/**
 * Clears all authentication data from localStorage and cookies
 * This is critical for proper logout and preventing token reuse
 */
const clearAuthFromStorage = () => {
  // Clear localStorage
  storage.removeItem("user");
  storage.removeItem("organizations");
  storage.removeItem("currentOrganization");
  storage.removeItem("verificationEmail");
  storage.removeItem("site_roles_cache");

  // Clear cookies from client-side (httpOnly cookies are cleared by server)
  // We use a workaround to signal server to clear httpOnly cookies
  if (typeof window !== "undefined") {
    // Remove any client-side token copies if they exist
    storage.removeItem("accessToken");
    storage.removeItem("refreshToken");
    storage.removeItem("_onboarding_complete");
    storage.removeItem("_welcome_complete");
  }
};

const setCurrentOrganizationWithValidation = (
  state: AuthState,
  responseOrganizations: Organization[] | undefined,
  responseCurrentOrganization: Organization | undefined,
): void => {
  if (responseCurrentOrganization) {
    state.currentOrganization = responseCurrentOrganization;
    return;
  }

  if (typeof window !== "undefined") {
    const savedOrgStr = storage.getItem("currentOrganization");
    if (savedOrgStr) {
      try {
        const savedOrg = JSON.parse(savedOrgStr);
        const orgExists = responseOrganizations?.some(
          (org) => org.id === savedOrg.id,
        );
        if (orgExists) {
          state.currentOrganization = savedOrg;
          return;
        }
      } catch (error) {
        console.error("Failed to parse saved organization:", error);
      }
    }
  }

  if (responseOrganizations && responseOrganizations.length > 0) {
    state.currentOrganization = responseOrganizations[0];
    return;
  }

  state.currentOrganization = null;
};

export const initiateLogin = createAsyncThunk(
  "auth/initiateLogin",
  async (credentials: LoginCredentials) => {
    return await AuthService.login(credentials);
  },
);

export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (verifyData: VerifyEmailData) => {
    return await AuthService.verifyEmail(verifyData);
  },
);

export const googleAuth = createAsyncThunk(
  "auth/googleAuth",
  async (googleData: GoogleAuthData) => {
    return await AuthService.googleAuth(googleData);
  },
);

export const resendCode = createAsyncThunk(
  "auth/resendCode",
  async ({ email, slug }: { email: string; slug?: string }) => {
    return await AuthService.resendCode(email, slug);
  },
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async ({ profileData }: { profileData: UpdateProfileData }) => {
    return await AuthService.updateProfile(profileData);
  },
);

export const createOrganization = createAsyncThunk(
  "auth/createOrganization",
  async (organizationData: CreateOrganizationData) => {
    return await AuthService.createOrganization(organizationData);
  },
);

export const completeWelcome = createAsyncThunk(
  "auth/completeWelcome",
  async () => {
    return await AuthService.completeWelcome();
  },
);

export const completeOnboarding = createAsyncThunk(
  "auth/completeOnboarding",
  async () => {
    return await AuthService.completeOnboarding();
  },
);

/**
 * Logout thunk that properly clears all authentication state
 * The API client will handle the server-side logout and cookie clearing
 */
export const logoutUser = createAsyncThunk("auth/logout", async () => {
  try {
    await AuthService.logout();
    // Clear all auth data after successful server logout
    clearAuthFromStorage();
    return null;
  } catch {
    // Even if server logout fails, clear client-side auth data
    // This ensures user is logged out locally
    clearAuthFromStorage();
    // Don't reject, so the reducer still clears the state
    return null;
  }
});

export const refreshAccessToken = createAsyncThunk(
  "auth/refreshToken",
  async () => {
    return await AuthService.refreshToken();
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    startLogout: (state) => {
      state.isLoggingOut = true;
      state.error = null;
    },
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        organizations?: Organization[];
        currentOrganization?: Organization;
        onboardingCompleted?: boolean;
        welcomeCompleted?: boolean;
      }>,
    ) => {
      state.user = action.payload.user;
      state.isAuthenticated = true;

      if (action.payload.organizations) {
        state.organizations = action.payload.organizations;
      }

      setCurrentOrganizationWithValidation(
        state,
        action.payload.organizations,
        action.payload.currentOrganization,
      );

      if (action.payload.onboardingCompleted !== undefined) {
        state.onboardingCompleted = action.payload.onboardingCompleted;
      }

      if (action.payload.welcomeCompleted !== undefined) {
        state.welcomeCompleted = action.payload.welcomeCompleted;
      }

      saveAuthToStorage(
        action.payload.user,
        action.payload.organizations,
        state.currentOrganization,
      );
    },

    setCurrentOrganization: (state, action: PayloadAction<Organization>) => {
      state.currentOrganization = action.payload;
      state.siteRoles = {};
      saveAuthToStorage(state.user, state.organizations, action.payload);
    },

    setSiteRole: (
      state,
      action: PayloadAction<{
        siteId: number;
        role: string;
        isOrganizationOwner: boolean;
        timestamp: number;
      }>,
    ) => {
      state.siteRoles[action.payload.siteId] = {
        role: action.payload.role,
        isOrganizationOwner: action.payload.isOrganizationOwner,
        timestamp: action.payload.timestamp,
      };
    },

    clearSiteRole: (state, action: PayloadAction<number>) => {
      delete state.siteRoles[action.payload];
    },

    clearAllSiteRoles: (state) => {
      state.siteRoles = {};
    },

    setAllSiteRoles: (
      state,
      action: PayloadAction<
        Record<
          number,
          { role: string; isOrganizationOwner: boolean; timestamp: number }
        >
      >,
    ) => {
      state.siteRoles = { ...state.siteRoles, ...action.payload };
    },

    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoggingOut = false;
      state.error = null;
      state.requiresVerification = false;
      state.verificationEmail = null;
      state.organizations = [];
      state.currentOrganization = null;
      state.onboardingCompleted = false;
      state.welcomeCompleted = false;
      state.siteRoles = {};
    },

    clearError: (state) => {
      state.error = null;
    },

    clearVerification: (state) => {
      state.requiresVerification = false;
      state.verificationEmail = null;
      storage.removeItem("verificationEmail");
    },

    recoverAuthState: (state) => {
      state.isLoggingOut = false;
      state.isLoading = false;

      if (!state.user && state.isAuthenticated) {
        state.isAuthenticated = false;
        state.organizations = [];
        state.currentOrganization = null;
        state.siteRoles = {};

        if (typeof window !== "undefined") {
          localStorage.removeItem("user");
          localStorage.removeItem("organizations");
          localStorage.removeItem("currentOrganization");
          localStorage.removeItem("site_roles_cache");
          localStorage.removeItem("persist:site");
          localStorage.removeItem("persist:asset");
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initiateLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requiresVerification = true;
        state.verificationEmail = action.payload.email;
        storage.setItem("verificationEmail", action.payload.email);
      })
      .addCase(initiateLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.welcomeCompleted = action.payload.user.isWelcomeCompleted;
        state.onboardingCompleted = action.payload.user.onboardingCompleted;

        const organizations =
          action.payload.user.organizations ||
          action.payload.organizations ||
          [];
        state.organizations = organizations;

        setCurrentOrganizationWithValidation(state, organizations, undefined);

        saveAuthToStorage(
          action.payload.user,
          organizations,
          state.currentOrganization,
        );
        storage.removeItem("verificationEmail");
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(googleAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(googleAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.welcomeCompleted = action.payload.user.isWelcomeCompleted;
        state.onboardingCompleted = action.payload.user.onboardingCompleted;

        const organizations =
          action.payload.user.organizations ||
          action.payload.organizations ||
          [];
        state.organizations = organizations;

        setCurrentOrganizationWithValidation(state, organizations, undefined);

        saveAuthToStorage(
          action.payload.user,
          organizations,
          state.currentOrganization,
        );
      })
      .addCase(googleAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(resendCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resendCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.welcomeCompleted = action.payload.user.isWelcomeCompleted;
        state.onboardingCompleted = action.payload.user.onboardingCompleted;
        if (state.user) {
          storage.setItem("user", JSON.stringify(state.user));
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations.push(action.payload.organization);
        state.currentOrganization = action.payload.organization;
        saveAuthToStorage(
          state.user,
          state.organizations,
          state.currentOrganization,
        );
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(completeWelcome.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeWelcome.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.welcomeCompleted = action.payload.user.isWelcomeCompleted;

        if (state.user) {
          storage.setItem("user", JSON.stringify(state.user));
        }
      })
      .addCase(completeWelcome.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      .addCase(completeOnboarding.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.onboardingCompleted = action.payload.user.onboardingCompleted;

        if (state.user) {
          storage.setItem("user", JSON.stringify(state.user));
        }
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.isLoading = false;
        state.error = handleApiError(action.payload);
      })
      /**
       * logoutUser thunk handlers
       * Ensures state is cleared regardless of success/failure
       */
      .addCase(logoutUser.pending, (state) => {
        state.isLoggingOut = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoggingOut = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.organizations = [];
        state.currentOrganization = null;
        state.onboardingCompleted = false;
        state.welcomeCompleted = false;
        state.siteRoles = {};
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even on rejection, clear the state locally
        // This prevents a logged-out user from accessing protected routes
        state.isLoggingOut = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.requiresVerification = false;
        state.verificationEmail = null;
        state.organizations = [];
        state.currentOrganization = null;
        state.onboardingCompleted = false;
        state.welcomeCompleted = false;
        state.siteRoles = {};
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        // Token refresh successful, update organizations in case they changed
        if (action.payload.organizations) {
          state.organizations = action.payload.organizations;
          saveAuthToStorage(
            state.user,
            action.payload.organizations,
            state.currentOrganization,
          );
        }
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        // Token refresh failed, logout the user
        state.user = null;
        state.isAuthenticated = false;
        state.isLoggingOut = false;
        state.siteRoles = {};
        clearAuthFromStorage();
      });
  },
});

export const {
  startLogout,
  setCredentials,
  setCurrentOrganization,
  setSiteRole,
  clearSiteRole,
  clearAllSiteRoles,
  setAllSiteRoles,
  logout,
  clearError,
  clearVerification,
  recoverAuthState,
} = authSlice.actions;

export default authSlice.reducer;
